/** Minimal PDF.js surface consumed by the application canvas viewer. */
export interface PdfViewport {
  width: number;
  height: number;
}

export interface PdfRenderTask {
  promise: Promise<void>;
  cancel(): void;
}

export interface PdfPage {
  getViewport(options: { scale: number }): PdfViewport;
  render(options: {
    canvas: HTMLCanvasElement | null;
    canvasContext?: CanvasRenderingContext2D;
    viewport: PdfViewport;
    transform?: number[];
  }): PdfRenderTask;
}

export interface PdfDocument {
  numPages: number;
  getPage(pageNumber: number): Promise<PdfPage>;
  destroy(): Promise<void>;
}

export type PDFDocumentProxy = PdfDocument;
export type PDFPageProxy = PdfPage;

interface PdfLoadingTask {
  promise: Promise<Omit<PdfDocument, 'destroy'>>;
  destroy(): Promise<void>;
}

interface PdfJsModule {
  version: string;
  GlobalWorkerOptions: { workerSrc: string };
  getDocument(options: Record<string, unknown>): PdfLoadingTask;
}

const PDFJS_VERSION = '6.4.299';
let modulePromise: Promise<PdfJsModule> | undefined;

/** PDF bytes and renderer assets stay in the current browser origin. */
export async function loadPdfDocument(bytes: Uint8Array): Promise<PdfDocument> {
  const assetBase = new URL(`/pdfjs/${PDFJS_VERSION}/`, window.location.origin).href;
  if (!modulePromise) {
    const moduleUrl = `${assetBase}pdf.mjs`;
    modulePromise = (import(/* webpackIgnore: true */ moduleUrl) as Promise<PdfJsModule>)
      .then(pdfjs => {
        if (pdfjs.version !== PDFJS_VERSION) throw new Error('Phiên bản trình đọc PDF không đồng bộ. Vui lòng tải lại trang.');
        pdfjs.GlobalWorkerOptions.workerSrc = `${assetBase}pdf.worker.min.mjs`;
        return pdfjs;
      }).catch(error => { modulePromise = undefined; throw error; });
  }
  const pdfjs = await modulePromise;
  const task = pdfjs.getDocument({
    // PDF.js transfers its input to the worker; retain the caller's download bytes.
    data: bytes.slice(),
    cMapUrl: `${assetBase}cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `${assetBase}standard_fonts/`,
    wasmUrl: `${assetBase}wasm/`,
    isEvalSupported: false,
    enableXfa: false,
    // Only the rendering API is used. No scripting manager or PDF action execution.
  });
  try {
    const document = await task.promise;
    return {
      numPages: document.numPages,
      getPage: pageNumber => document.getPage(pageNumber),
      destroy: () => task.destroy(),
    };
  } catch (error) {
    await task.destroy().catch(() => undefined);
    throw error;
  }
}
